import { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../../assets/scss/app.scss";
import "./LeafletMap.scss";

import { ReactComponent as CrimeIcon } from "../../assets/images/crime-icon.svg";
import { ReactComponent as HospitalsIcon } from "../../assets/images/hospital-icon.svg";
import { ReactComponent as WalletIcon } from "../../assets/images/wallet-add.svg";
import { ReactComponent as KeyIcon } from "../../assets/images/key.svg";
import { ReactComponent as BriefcaseIcon } from "../../assets/images/briefcase.svg";
import { ReactComponent as MediaSalaryIcon } from "../../assets/images/subscription.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getHospitalCountData,
  mapAnalytics,
} from "../../redux/slices/comparison.Slice";
import { useDispatch, useSelector } from "react-redux";
import ActionModal from "../../pages/comparison/ActionModal";
import CityDetailLimitModal from "../../pages/restriction-modals/CityDetailLimitModal";
import { getFeatureAccess } from "../../utils/helpers/checkSubRestriction";
import { sessionId } from "../../redux/slices/cityComparisonSlice";
import { useTheme } from "../../context/ThemeContext";

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Create custom hospital marker icon — water-drop pin, color set per role.
const createHospitalIcon = (markerColor) => {
  try {
    return L.divIcon({
      className: "custom-hospital-marker",
      html: `
        <svg viewBox="0 0 24 30" width="30" height="38" style="display:block;filter:drop-shadow(0 2px 3px rgba(15,23,42,0.25))">
          <path d="M12 0a10 10 0 0 1 10 10c0 7.5-10 20-10 20S2 17.5 2 10A10 10 0 0 1 12 0z" fill="${markerColor}"/>
          <circle cx="12" cy="10" r="5" fill="#ffffff"/>
          <path d="M11 7h2v2h2v2h-2v2h-2v-2H9V9h2z" fill="${markerColor}"/>
        </svg>
      `,
      iconSize: [30, 38],
      iconAnchor: [15, 38],
      popupAnchor: [0, -36],
    });
  } catch (error) {
    console.error("Error creating hospital icon:", error);
    return null;
  }
};

// Helper component to access map instance in React Leaflet v5.
// Also fixes the "grey strip on one side" symptom by calling
// invalidateSize() whenever the map container changes size — Leaflet
// computes tile coverage from the container dimensions at init time,
// so any layout shift after that (sidebar reflow, font load, dashboard
// content settling, viewport resize) leaves a swath of tiles unloaded
// until the map is told to recheck.
const MapInstanceRecorder = ({ setMapRef }) => {
  const map = useMap();
  useEffect(() => {
    setMapRef(map);
    if (!map) return undefined;
    const refresh = () => {
      try {
        map.invalidateSize();
      } catch (err) {
        // map may be torn down mid-tick during unmount
      }
    };
    // Initial nudges: once on the next tick (after the surrounding
    // layout paints) and again ~300ms later to catch slower reflows
    // (web font swap, lazy-mounted siblings).
    const t1 = setTimeout(refresh, 0);
    const t2 = setTimeout(refresh, 300);
    window.addEventListener("resize", refresh);
    let observer = null;
    const container = map.getContainer && map.getContainer();
    if (container && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(refresh);
      observer.observe(container);
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", refresh);
      if (observer) observer.disconnect();
    };
  }, [map, setMapRef]);
  return null;
};

const LeafletMap = ({
  selectedRole,
  city,
  state,
  hospitalData,
  tileProvider,
  planExpired,
  userPlanData,
  restrictedData,
}) => {
  const [hospitalIcon, setHospitalIcon] = useState(null);
  const [userPlan, setUserPlan] = useState({
    plan: "Free",
    expiryDate: "",
  });
  const [isClient, setIsClient] = useState(false);
  // const [messageType, setMessageType] = useState<'role' | 'limit'>('role');
  const [messageType, setMessageType] = useState("role");
  const [subAlertModal, setSubAlertModal] = useState(false);
  const [subText, setSubText] = useState({
    header: "",
    desc: "",
    isCityAdd: false,
  });
  const [upgradeSource, setUpgradeSource] = useState("");
  const [isRestrictedData, setIsRestrictedData] = useState(restrictedData);
  const {
    __planExpired,
    role_selection_restriction_1,
    role_selection_restriction_2,
  } = getFeatureAccess(userPlan);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const dispatch = useDispatch();
  const store = useSelector((state) => state.profile);
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  const roleColors = {
    4: "#0057D9", // Nurse Anesthetists (CRNA)
    5: "#0081F2", // Nurse Midwives
    6: "#00A6FB", // Nurse Practitioners
    3: "#1BAE70", // Medical & Health Services Managers
    9: "#2ECC71", // Registered Nurses
    8: "#F2C94C", // Nursing Instructors/Teachers
    2: "#F2994A", // LPN/LVN
    7: "#EB5757", // Nursing Assistants
    1: "#C0392B", // Home Health Aides
  };

  const zoom = 11;
  useEffect(() => {
    setIsRestrictedData(restrictedData);
  }, [restrictedData]);

  // Default to Sacramento if no valid coordinates are found
  const center = useMemo(() => {
    if (
      hospitalData?.length > 0 &&
      hospitalData[0]?.latitude &&
      hospitalData[0]?.longitude
    ) {
      return {
        lat: hospitalData[0]?.latitude,
        lng: hospitalData[0]?.longitude,
      };
    }
    return { lat: 38.5816, lng: -121.4944 }; // fallback only if no hospitals
  }, [hospitalData]);

  // useEffect(() => {
  //   if (mapRef.current && hospitalData?.length > 0) {
  //     const bounds = L.latLngBounds(
  //       hospitalData.map((h) => [h.latitude, h.longitude])
  //     );
  //     mapRef.current.fitBounds(bounds, { padding: [50, 50] });
  //   }
  // }, [hospitalData]);
  useEffect(() => {
    if (mapRef.current && hospitalData?.length > 0) {
      const bounds = L.latLngBounds(
        hospitalData.map((h) => [h.latitude, h.longitude]),
      );

      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // The extra bottom padding is meant for tall full-screen maps (where a
        // bottom sheet/list overlaps the map). On a small map — like the
        // dashboard hospital card — that padding is larger than the container,
        // so fitBounds over-zooms and pushes every marker out of view. Clamp
        // the padding to the actual map size so all markers stay visible.
        const size = mapRef.current.getSize();
        const topPad = Math.min(50, Math.floor(size.y * 0.15));
        // Keep at least ~40% of the height for the markers themselves.
        const maxBottom = Math.max(topPad, Math.floor(size.y * 0.45) - topPad);
        const bottomPad = Math.min(350, maxBottom);
        const sidePad = Math.min(50, Math.floor(size.x * 0.15));

        mapRef.current.fitBounds(bounds, {
          paddingTopLeft: [sidePad, topPad],
          paddingBottomRight: [sidePad, bottomPad],
        });
      } else {
        mapRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [hospitalData]);

  useEffect(() => {
    if (store?.profileItem) {
      setUserPlan({
        plan: store?.profileItem?.plan?.name,
        expiryDate: store?.profileItem?.plan_expiry_date,
      });
    }
  }, [store?.profileItem]);

  const _handleCoundData = async (id) => {
    try {
      let obj = {
        hospital_id: id,
      };
      const result = await dispatch(getHospitalCountData(obj));
      if (result?.payload?.hospitalCountItems) {
        setIsRestrictedData({
          is_comparisan: result?.payload?.hospitalCountItems?.is_comparisan,
          comparisanMessage:
            result?.payload?.hospitalCountItems?.comparisanMessage,
          header: result?.payload?.hospitalCountItems?.header,
          messageBody1: result?.payload?.hospitalCountItems?.messageBody1,
          messageBody2: result?.payload?.hospitalCountItems?.messageBody2,
        });
      }
    } catch (error) {
    } finally {
    }
  };

  // Tile layer configurations

  const tileProviders = {
    openstreetmap: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
    cartodb: {
      url:
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
    stamen: {
      url:
        "https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}{r}.png",
      attribution:
        'Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
    dark: {
      url:
        "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
  };

  // Regardless of which light-mode style is configured (openstreetmap /
  // cartodb / stamen), switch to the dark basemap whenever the app is in
  // dark mode — a light tile set would otherwise sit as a bright rectangle
  // in an otherwise dark UI.
  const selectedProvider =
    resolvedTheme === "dark"
      ? tileProviders.dark
      : tileProviders[tileProvider] || tileProviders.openstreetmap;

  useEffect(() => {
    setIsClient(true);
    const color = roleColors[selectedRole?.value || selectedRole] || "#0195D3";
    const icon = createHospitalIcon(color);
    setHospitalIcon(icon);
  }, [selectedRole]);

  const showSubModal = (type) => {
    setMessageType(type);

    let header = "Subscription Required!";
    let desc = "";

    if (type === "limit") {
      desc = isRestrictedData?.comparisanMessage;
    } else {
      if (planExpired) {
        desc =
          "Your plan has expired. Subscribe now to unlock all the benefits.";
      } else {
        const subscriptionType = "role_selection_restriction_2"
          ? "Pro"
          : "Plus/Pro";
        desc = `This feature is available with our ${subscriptionType} subscription. Would you like to subscribe now and unlock all the benefits?`;
      }
    }

    setSubText({
      header,
      desc,
      isCityAdd: false,
    });

    setTimeout(() => {
      setSubAlertModal(true);
    }, 500);
  };

  // const handleMarkerClick = (hospital, markerRef) => {
  //   const map = mapRef.current;
  //   if (!map || !markerRef) return;

  //   const { latitude: lat, longitude: lng } = hospital;
  //   const targetZoom = Math.max(map.getZoom(), 14);

  //   const isMobile = window.innerWidth <= 768;

  //   if (isMobile) {
  //     const point = map.latLngToContainerPoint([lat, lng]);
  //     const popupHeight = 300;
  //     const offsetY = popupHeight / 2 + 50;

  //     point.y += offsetY;
  //     const newCenter = map.containerPointToLatLng(point);

  //     map
  //       .flyTo(newCenter, targetZoom, {
  //         duration: 0.8,
  //       })
  //       .once("moveend", () => {
  //         markerRef.openPopup();
  //       });
  //   } else {
  //     map
  //       .flyTo([lat, lng], targetZoom, {
  //         duration: 0.8,
  //       })
  //       .once("moveend", () => {
  //         markerRef.openPopup();
  //       });
  //   }
  // };

  const handleMarkerClick = (hospital, markerRef) => {
    const map = mapRef.current;
    if (!map || !markerRef) return;

    const session_id = sessionStorage.getItem("comparison_session_id");

    // ✅ 1️⃣ FIRST API — Clicked Hospital Pin
    dispatch(
      mapAnalytics({
        session_id,
        event_type: "pin_clicked",
        user_id: user?.id || null,
        hospital_name: hospital?.hospital_name,
        city: hospital?.city,
        state: hospital?.state,
      }),
    );

    const { latitude: lat, longitude: lng } = hospital;
    const targetZoom = Math.max(map.getZoom(), 14);
    const isMobile = window.innerWidth <= 768;

    const flyAction = () => {
      markerRef.openPopup();

      dispatch(
        mapAnalytics({
          session_id,
          event_type: "infotip_viewed",
          user_id: user?.id || null,
          hospital_name: hospital?.hospital_name,
          city: hospital?.city,
          state: hospital?.state,
        }),
      );
    };

    if (isMobile) {
      const point = map.latLngToContainerPoint([lat, lng]);
      const popupHeight = 300;
      const offsetY = popupHeight / 2 + 50;

      point.y += offsetY;
      const newCenter = map.containerPointToLatLng(point);

      map
        .flyTo(newCenter, targetZoom, { duration: 0.8 })
        .once("moveend", flyAction);
    } else {
      map
        .flyTo([lat, lng], targetZoom, { duration: 0.8 })
        .once("moveend", flyAction);
    }
  };
  const handleHospitalClick = async (hospital) => {
    const session_id = sessionStorage.getItem("comparison_session_id");

    try {
      const obj = { hospital_id: hospital?.id, is_count: true };
      const result = await dispatch(getHospitalCountData(obj)).unwrap();

      const data = result?.hospitalCountItems;

      if (!data) {
        // ✅ LIMIT HIT → send hit_daily_limit true
        dispatch(
          mapAnalytics({
            session_id,
            event_type: "detail_clicked",
            hospital_name: hospital?.hospital_name,
            city: hospital?.city,
            state: hospital?.state,
            user_id: user?.id || null,
            hit_daily_limit: true,
          }),
        );

        setUpgradeSource("hospital_detail_limit");
        setSubAlertModal(true);
        return;
      }

      setIsRestrictedData({
        is_comparisan: data.is_comparisan,
        comparisanMessage: data.comparisanMessage,
        header: data.header,
        messageBody1: data.messageBody1,
        messageBody2: data.messageBody2,
        comparisanMaxCount: data.comparisanMaxCount,
        remaining_count: data.remaining_count,
      });

      if (data.incrementSuccess === true) {
        // ✅ SUCCESS → normal detail click
        dispatch(
          mapAnalytics({
            session_id,
            hospital_name: hospital?.hospital_name,
            city: hospital?.city,
            state: hospital?.state,
            event_type: "detail_clicked",
            user_id: user?.id || null,
          }),
        );

        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("trackCustom", "ViewedHospital");
          console.log("ViewedHospital fired after delay");
        }

        navigate(`/search/details/${hospital?.city_id}`, {
          state: {
            from: "hospital_map",
            hospitalMapData: hospital,
            pathname: window.location.pathname,
          },
        });
      } else {
        // ✅ LIMIT HIT → send hit_daily_limit true
        dispatch(
          mapAnalytics({
            session_id,
            hospital_name: hospital?.hospital_name,
            city: hospital?.city,
            state: hospital?.state,
            event_type: "detail_clicked",
            user_id: user?.id || null,
            hit_daily_limit: true,
          }),
        );

        setUpgradeSource("hospital_detail_limit");
        setSubAlertModal(true);
      }
    } catch (error) {
      console.error("Error fetching hospital count:", error);

      // ✅ ERROR = treat as limit
      dispatch(
        mapAnalytics({
          session_id,
          hospital_name: hospital?.hospital_name,
          city: hospital?.city,
          state: hospital?.state,
          event_type: "detail_clicked",
          user_id: user?.id || null,
          hit_daily_limit: true,
        }),
      );

      setSubAlertModal(true);
    }
  };
  // const handleHospitalClick = async (hospital) => {
  //   try {
  //     const obj = { hospital_id: hospital?.id, is_count: true };
  //     const result = await dispatch(getHospitalCountData(obj)).unwrap();

  //     const data = result?.hospitalCountItems;

  //     if (!data) {
  //       setUpgradeSource("hospital_detail_limit");

  //       // showSubModal("limit");
  //       setSubAlertModal(true);
  //       return;
  //     }

  //     setIsRestrictedData({
  //       is_comparisan: data.is_comparisan,
  //       comparisanMessage: data.comparisanMessage,
  //       header: data.header,
  //       messageBody1: data.messageBody1,
  //       messageBody2: data.messageBody2,
  //       comparisanMaxCount: data.comparisanMaxCount,
  //       remaining_count: data.remaining_count,
  //     });

  //     if (data.incrementSuccess === true) {
  //       if (typeof window !== "undefined" && window.fbq) {
  //         window.fbq("trackCustom", "ViewedHospital");
  //         console.log("ViewedHospital fired after delay");
  //       }

  //       navigate(`/search/details/${hospital?.city_id}`, {
  //         state: {
  //           from: "hospital_map",
  //           hospitalMapData: hospital,
  //           pathname: window.location.pathname,
  //         },
  //       });
  //     } else {
  //       // showSubModal("limit");
  //       setUpgradeSource("hospital_detail_limit");

  //       setSubAlertModal(true);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching hospital count:", error);
  //     // showSubModal("limit");
  //     setSubAlertModal(true);
  //   }
  // };

  const user = useSelector((state) => state.auth?.user);

  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("comparison_session_id");
      sessionStorage.removeItem("session_expiry");

      console.log("🧹 Session cleared on logout");
    }
  }, [user]);
  useEffect(() => {
    if (!user?.id) return;

    const existingSession = sessionStorage.getItem("comparison_session_id");
    const expiry = sessionStorage.getItem("session_expiry");

    // Only check if session exists, don't create a new one
    // Session creation is handled by Dashboard component
    if (existingSession && expiry && Date.now() < Number(expiry)) {
      
      return;
    }

    // Check if session is being created (flag set by Dashboard)
    const isCreatingSession = sessionStorage.getItem("session_creating");
    if (isCreatingSession === "true") {
      console.log("🛑 Session creation in progress, skipping in LeafletMap");
      return;
    }

    // Only create session if Dashboard hasn't already started
    const dashboardMounted = sessionStorage.getItem("dashboard_mounted");
    if (dashboardMounted === "true") {
      console.log("🛑 Dashboard handles session, skipping in LeafletMap");
      return;
    }

    // Fallback: create session only if Dashboard isn't present
    console.log("✅ Creating session from LeafletMap (fallback)");
    sessionStorage.setItem("session_creating", "true");
    
    dispatch(sessionId()).then((res) => {
      const data = res.payload?.data;

      if (data?.session_id) {
        sessionStorage.setItem("comparison_session_id", data.session_id);

        const expiryTime = Date.now() + data.expires_in * 1000;
        sessionStorage.setItem("session_expiry", expiryTime);

        
      }
      sessionStorage.removeItem("session_creating");
    }).catch(() => {
      sessionStorage.removeItem("session_creating");
    });
  }, [user?.id, dispatch]);

  if (!isClient) {
    return (
      <div className="leaflet-map-wrapper">
        <div className="map-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading map...</span>
          </div>
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="leaflet-map-wrapper">
      <MapContainer
        center={center}
        zoom={zoom}
        className="leaflet-map-container"
        zoomControl={true}
        scrollWheelZoom={true}
        ref={mapRef}
      >
        <MapInstanceRecorder
          setMapRef={(mapInstance) => {
            mapRef.current = mapInstance;
            window.leafletMapInstance = mapInstance;
          }}
        />
        <TileLayer
          url={selectedProvider.url}
          attribution={selectedProvider.attribution}
        />

        {hospitalData?.map((hospital, index) => {
          const lat = hospital?.latitude;
          const lng = hospital?.longitude;
          if (isNaN(lat) || isNaN(lng)) return null;
          const roleKey = selectedRole?.value ?? selectedRole ?? "default";
          return (
            <Marker
              key={`${hospital.id}-${roleKey}`}
              position={[lat, lng]}
              icon={hospitalIcon || undefined}
              ref={(ref) => {
                if (ref) {
                  markersRef.current[hospital.id] = ref;
                } else {
                  delete markersRef.current[hospital.id];
                }
              }}
              eventHandlers={{
                click: (e) => {
                  // Prevent default popup behavior
                  e.target.closePopup();
                  // Handle centering and opening popup
                  handleMarkerClick(hospital, e.target);
                },
                popupopen: (e) => {
                  e.target.setOpacity(0.7);
                  const popup = e.popup || e.target._popup;
                  const container = popup?._container;
                  if (container) {
                    container.classList.remove("popup-animate-in");
                    void container.offsetWidth; // force reflow so animation replays
                    container.classList.add("popup-animate-in");
                  }
                },
                popupclose: (e) => {
                  e.target.setOpacity(1);
                  const popup = e.popup || e.target._popup;
                  const container = popup?._container;
                  if (container) {
                    container.classList.remove("popup-animate-in");
                  }
                },
              }}
            >
              <Popup
                className="custom-hospital-popup dashboard-card-popup"
                closeButton={false}
              >
                <div
                  className="card city-all-content h-100"
                  onClick={() => handleHospitalClick(hospital)}
                >
                  <div className="card-body p-0">
                    <div className="city-details">
                      <div className="title-btn">
                        <div className="inner-sub-sub-title w-100 p-0">
                          <div className="title-subtitle">
                            <div className="inner-sub-title">
                              {hospital?.hospital_name}
                            </div>
                            <div className="inner-sub-subtitle">
                              {hospital?.city},{hospital?.state}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="city-sub-details">
                        <div className="pricing-col">
                          <div className="contentwrap">
                            <div className="gross-salary">
                              <div className="gross-title">
                                <WalletIcon /> Median Salary
                              </div>
                              <div className="gross-price">
                                {/* ${hospital?.median_salary} */}$
                                {Number(
                                  hospital?.median_salary || 0,
                                ).toLocaleString("en-US")}
                                <span className="text-secondary">/yr</span>
                              </div>
                            </div>
                            <div className="home-price">
                              <div className="gross-title">
                                <BriefcaseIcon /> Home price
                              </div>
                              <div className="gross-price">
                                $
                                {Number(
                                  hospital?.home_prices || 0,
                                ).toLocaleString("en-US")}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="pricing-col">
                          <div className="contentwrap">
                            <div className="gross-salary">
                              <div className="gross-title">
                                <KeyIcon /> Rent
                              </div>
                              <div className="gross-price">
                                $
                                {Number(
                                  hospital?.average_monthly_rent_prices || 0,
                                ).toLocaleString("en-US")}
                                <span className="text-secondary">/mo</span>
                              </div>
                            </div>
                            <div className="home-price">
                              <div className="gross-title">
                                <BriefcaseIcon /> Est Take-Home
                              </div>
                              <div className="gross-price">
                                $
                                {Number(
                                  hospital?.est_home_salary || 0,
                                ).toLocaleString("en-US")}
                                <span className="text-secondary">/yr</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="pricing-col">
                          <div className="contentwrap">
                            <div className="gross-salary">
                              <div className="gross-title">
                                <MediaSalaryIcon /> Mortagage ={" "}
                                <b>{hospital?.mortgage_percent}%</b> of income
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="crimerate-hospitals">
                        <div className="crimerate">
                          <CrimeIcon />
                          <span className="label">Crime Rate</span>&nbsp;&nbsp;
                          <span
                            className="crime"
                            style={{
                              color:
                                hospital?.crime === "Severe" ||
                                hospital?.crime === "Moderate"
                                  ? hospital?.crime_color_code
                                  : "green",
                            }}
                          >
                            {hospital?.crime}
                          </span>
                        </div>
                        <div className="hospitals">
                          <div className="hospital-info">
                            <div className="left-section d-flex align-items-center justify-content-between w-100">
                              <div className="hospital">
                                <HospitalsIcon />
                                {hospital.count
                                  .toString()
                                  .padStart(2, "0")}{" "}
                                Hospital(s) nearby
                              </div>
                              <div
                                className="home-price text-right"
                                style={{ color: "#0195D3" }}
                                // onClick={() => handleHospitalClick(hospital)}
                                onClick={(e) => {
                                  e.stopPropagation(); // prevent double trigger if nested
                                  handleHospitalClick(hospital);
                                }}
                              >
                                View details
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      <CityDetailLimitModal
        isVisible={subAlertModal}
        onClose={() => {
          setMessageType("role");
          setSubAlertModal(false);
        }}
        eventSource={upgradeSource}
      />
    </div>
  );
};

export default LeafletMap;
